import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock, Users, AlertTriangle, MessageSquare, Printer,
  UserCheck, Calendar as CalendarIcon, ChevronRight, ChevronDown,
  Cake, Shirt, RefreshCcw, Briefcase, CheckCircle2, X,
  UserX, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import useMessageStream from '../hooks/useMessageStream';
import PartyTimer from './PartyTimer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTY_COLORS = ['#FF3B6B', '#06D6A0', '#FFD166', '#6366F1', '#FF7A2E', '#EC4899'];

const SectionHeader = ({ icon: Icon, title, count, accent, expanded, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={expanded}
    className="w-full flex items-center gap-2 mt-4 first:mt-0 text-left group rounded-md hover:bg-[#FFFDF8] px-1 py-1 -mx-1 transition-colors"
    data-testid={testId}
  >
    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}15` }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <h4 className="text-sm font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{title}</h4>
    <span className="text-[11px] text-[#6B6B7A]">({count})</span>
    <span className="ml-auto text-[#6B6B7A] group-hover:text-[#1E1E24]" aria-hidden>
      {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </span>
  </button>
);

// ------------------- Booking Details Modal -------------------
const BookingDetailsDialog = ({ bookingId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !bookingId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/admin/bookings/${bookingId}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load booking'))
      .finally(() => setLoading(false));
  }, [open, bookingId]);

  const b = data?.booking || {};
  const form = data?.allergy_form || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden" data-testid="booking-details-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E1E24]/10">
          <DialogTitle className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {b.child_name ? `${b.child_name} (${b.child_age}) · ${b.time_slot}` : 'Booking details'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A]">
            {b.package_name} · {b.guest_count} guests · {b.date}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {loading && <p className="text-sm text-[#6B6B7A]">Loading…</p>}
          {!loading && data && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Customer</p>
                  <p className="font-semibold text-[#1E1E24]">{b.customer_name}</p>
                  <p className="text-xs text-[#6B6B7A]">{b.customer_phone}</p>
                  <p className="text-xs text-[#6B6B7A]">{b.customer_email}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Host</p>
                  <p className="font-semibold text-[#1E1E24]">{b.booked_by_employee_name || 'Unassigned'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] mt-2">Status</p>
                  <p className="text-xs font-semibold text-[#1E1E24] capitalize">{b.status}</p>
                </div>
              </div>

              {form?.allergies?.length > 0 && (
                <div className="rounded-lg border-2 border-[#D1344D] bg-[#FFE4E1] p-3" data-testid="details-allergies">
                  <p className="text-xs font-semibold text-[#D1344D]">⚠ Allergies</p>
                  <p className="text-xs text-[#1E1E24] mt-0.5">{form.allergies.join(', ')}</p>
                  {form.special_notes && <p className="text-[11px] text-[#6B6B7A] mt-1">{form.special_notes}</p>}
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] mb-1">Add-ons</p>
                {(b.add_ons?.length || 0) === 0 ? (
                  <p className="text-xs italic text-[#9a9aa6]">None</p>
                ) : (
                  <ul className="text-xs text-[#1E1E24] space-y-0.5">
                    {b.add_ons.map((a, i) => (
                      <li key={i}>• {a.name} × {a.quantity || 1} · ${(a.price || a.unit_price || 0).toFixed(2)}</li>
                    ))}
                  </ul>
                )}
              </div>

              {(b.custom_charges?.length || 0) > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] mb-1">Custom Extras</p>
                  <ul className="text-xs text-[#1E1E24] space-y-0.5">
                    {b.custom_charges.map((c, i) => (
                      <li key={i}>• {c.name} × {c.quantity || 1} · ${(c.unit_price || 0).toFixed(2)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(b.designs?.length || 0) > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] mb-1">Custom Designs</p>
                  <ul className="text-xs text-[#1E1E24] space-y-0.5">
                    {b.designs.map((d, i) => (
                      <li key={i} className="capitalize">
                        • {d.garment === 'tshirt' ? 'T-Shirt' : d.garment} — {d.template_id || 'uploaded image'}
                        {d.label ? ` · ${d.label}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#E8E5DF]">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Subtotal</p>
                  <p className="font-semibold">${(b.subtotal || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">HST</p>
                  <p className="font-semibold">${(b.tax || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Total</p>
                  <p className="font-semibold text-[#FF3B6B]">${(b.total || 0).toFixed(2)}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Payment</p>
                <p className="text-xs text-[#1E1E24]">
                  Status: <b className="capitalize">{b.payment_status}</b> · Deposit: {b.deposit_paid ? '✓' : '—'} · Balance due: ${(b.balance_due || 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1E1E24]/10 flex items-center justify-end gap-2 bg-[#FFFDF8]">
          <Button
            type="button" variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#6B6B7A] hover:text-[#1E1E24]"
            data-testid="details-close-btn"
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={() => { onOpenChange(false); navigate(`/admin?booking=${bookingId}`); }}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-4"
            data-testid="details-open-full-btn"
          >
            Open in full dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ------------------- Shift Details Modal -------------------
const ShiftDetailsDialog = ({ shift, open, onOpenChange }) => {
  if (!shift) return null;
  const dur = (() => {
    try {
      const [sh, sm] = (shift.start_time || '00:00').split(':').map(Number);
      const [eh, em] = (shift.end_time || '00:00').split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      return mins > 0 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : '—';
    } catch { return '—'; }
  })();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md p-0 overflow-hidden" data-testid="shift-details-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E1E24]/10">
          <DialogTitle className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {shift.employee_name}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A] capitalize">
            {(shift.role || '—').replace('_', ' ')}
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Scheduled</p>
              <p className="font-semibold">{shift.start_time} – {shift.end_time}</p>
              <p className="text-xs text-[#6B6B7A]">Duration {dur}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Status</p>
              <p className="font-semibold capitalize">{shift.status || 'scheduled'}</p>
            </div>
          </div>
          {(shift.clock_in || shift.clock_out) && (
            <div className="rounded-lg bg-[#FFFDF8] border border-[#E8E5DF] p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] mb-1">Clocked</p>
              <p className="text-xs">In: {shift.clock_in || '—'}</p>
              <p className="text-xs">Out: {shift.clock_out || '—'}</p>
              {typeof shift.break_minutes === 'number' && <p className="text-xs">Break: {shift.break_minutes} min</p>}
            </div>
          )}
          {shift.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Notes</p>
              <p className="text-xs">{shift.notes}</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#1E1E24]/10 flex items-center justify-end bg-[#FFFDF8]">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="shift-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ------------------- Delete Booking Dialog (PIN-gated) -------------------
const DeleteBookingDialog = ({ booking, open, onOpenChange, onDeleted }) => {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setPin(''); }, [open]);

  if (!booking) return null;

  const handleDelete = async () => {
    if (!pin || pin.length < 4) {
      toast.error('Enter your 4-digit PIN or admin password');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/bulk-delete`,
        { section: 'bookings', ids: [booking.booking_id], pin },
        { withCredentials: true },
      );
      const cascade = res.data?.cascade || {};
      const cascCount = Object.values(cascade).reduce((a, b) => a + (Number(b) || 0), 0);
      toast.success(
        `Booking deleted${cascCount > 0 ? ` · ${cascCount} related record${cascCount === 1 ? '' : 's'} cascaded` : ''}`,
      );
      onOpenChange(false);
      onDeleted && onDeleted(booking.booking_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete booking');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md p-0 overflow-hidden" data-testid="runsheet-delete-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E1E24]/10">
          <DialogTitle className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Delete this booking?
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A]">
            {booking.child_name} ({booking.child_age}) · {booking.time_slot} · {booking.customer_name}
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-4 text-sm">
          <div className="rounded-lg border border-[#FF3B6B]/30 bg-[#FFF0F3] p-3 text-xs text-[#1E1E24] leading-relaxed">
            <p className="font-semibold text-[#D1344D] mb-1">Cascading delete</p>
            <p>
              This booking, its allergy form, transactions and any linked invoice will be
              soft-deleted. The action is logged in the audit trail and broadcast to every
              open CRM tab.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1E1E24] block mb-1">
              Confirm with your PIN or admin password
            </label>
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="4-digit PIN"
              className="bg-white"
              autoFocus
              data-testid="runsheet-delete-pin"
            />
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-[#1E1E24]/10 bg-[#FFFDF8] gap-2">
          <Button
            type="button" variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="text-[#6B6B7A] hover:text-[#1E1E24]"
            data-testid="runsheet-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={busy || !pin}
            className="rounded-full bg-[#D1344D] hover:bg-[#b9293f] text-white px-4 disabled:opacity-50"
            data-testid="runsheet-delete-confirm"
          >
            {busy ? 'Deleting…' : 'Delete booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------- Main widget -------------------
const TodayRunSheet = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ parties: [], shifts: [], special_tasks: [] });
  const [loading, setLoading] = useState(true);
  const [dateLabel, setDateLabel] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [doneTasks, setDoneTasks] = useState({}); // local checkmarks
  const [detailsBookingId, setDetailsBookingId] = useState(null);
  const [shiftDetail, setShiftDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Section collapse state — all expanded by default
  const [sections, setSections] = useState({ bookings: true, shifts: true, tasks: true });
  const toggleSection = (key) => setSections((s) => ({ ...s, [key]: !s[key] }));
  const setAllSections = (open) => setSections({ bookings: open, shifts: open, tasks: open });

  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/today-parties`, { withCredentials: true });
      setData({
        parties: res.data.parties || [],
        shifts: res.data.shifts || [],
        special_tasks: res.data.special_tasks || [],
      });
      setDateLabel(
        new Date(`${res.data.date}T00:00:00`).toLocaleDateString('en-CA', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        })
      );
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);
  useMessageStream(useCallback(() => { fetchToday(); }, [fetchToday]));

  const toggleArrived = async (bookingId, e) => {
    if (e) e.stopPropagation();
    setBusyId(bookingId);
    try {
      const res = await axios.patch(
        `${API_URL}/api/admin/bookings/${bookingId}/arrived`,
        {}, { withCredentials: true },
      );
      setData((prev) => ({
        ...prev,
        parties: prev.parties.map(p =>
          p.booking_id === bookingId
            ? { ...p, arrived: res.data.arrived, arrived_at: res.data.arrived_at || null }
            : p),
      }));
      toast.success(res.data.arrived ? 'Marked as arrived' : 'Un-marked arrived');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const messageHost = (p, e) => {
    if (e) e.stopPropagation();
    if (!p.booked_by_employee_id) {
      toast('No host assigned', { description: 'Assign one from the booking detail first.' });
      return;
    }
    navigate(`/crm/messages?employee_id=${p.booked_by_employee_id}`);
  };

  const toggleComplete = async (bookingId, currentlyComplete, e) => {
    if (e) e.stopPropagation();
    if (!currentlyComplete && !window.confirm('Mark this party as complete? This will queue inventory deductions for any printed/edible add-ons (you can apply or skip them on the Inventory page).')) {
      return;
    }
    setBusyId(bookingId);
    try {
      const url = currentlyComplete
        ? `${API_URL}/api/admin/bookings/${bookingId}/unmark-complete`
        : `${API_URL}/api/admin/bookings/${bookingId}/mark-complete`;
      const res = await axios.post(url, {}, { withCredentials: true });
      setData((prev) => ({
        ...prev,
        parties: prev.parties.map((p) =>
          p.booking_id === bookingId ? { ...p, party_completed: !currentlyComplete } : p),
      }));
      if (currentlyComplete) {
        toast.success('Party un-marked');
      } else {
        const created = res.data?.pending_deductions_created || 0;
        toast.success(created > 0
          ? `Party complete · ${created} inventory deduction${created === 1 ? '' : 's'} pending review`
          : 'Party complete');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const printChecklist = (p, e) => {
    if (e) e.stopPropagation();
    window.open(`/admin/runsheet/${p.booking_id}?print=1`, '_blank', 'noopener');
  };

  const markNoShow = async (p, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Mark ${p.child_name}'s party as No Show?\n\nThis sets the booking status to "no_show", clears the arrived flag and voids any pending transactions. Refunds (per the 50% no-show policy) must still be processed manually from the booking detail.`)) {
      return;
    }
    setBusyId(p.booking_id);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/bookings/${p.booking_id}/no-show`,
        {}, { withCredentials: true },
      );
      // Drop the row from the run-sheet — no_show parties no longer belong here.
      setData((prev) => ({
        ...prev,
        parties: prev.parties.filter((row) => row.booking_id !== p.booking_id),
      }));
      const voided = res.data?.voided_transactions || 0;
      toast.success(
        voided > 0
          ? `Marked as No Show · ${voided} pending transaction${voided === 1 ? '' : 's'} voided`
          : 'Marked as No Show',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as No Show');
    } finally {
      setBusyId(null);
    }
  };

  const openDelete = (p, e) => {
    if (e) e.stopPropagation();
    setDeleteTarget(p);
  };

  const onBookingDeleted = (bookingId) => {
    setData((prev) => ({
      ...prev,
      parties: prev.parties.filter((row) => row.booking_id !== bookingId),
    }));
    setDeleteTarget(null);
  };

  const { parties, shifts, special_tasks } = data;

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 mb-6 overflow-hidden" data-testid="today-runsheet">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF3B6B15' }}>
            <CalendarIcon className="w-5 h-5 text-[#FF3B6B]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Today&apos;s Run Sheet
            </h3>
            <p className="text-xs text-[#6B6B7A]">
              {dateLabel || 'Today'} · {parties.length} parties · {shifts.length} shifts · {special_tasks.length} tasks
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAllSections(true)}
            className="text-[11px] text-[#6B6B7A] hover:text-[#1E1E24] px-2 py-1 rounded"
            data-testid="runsheet-expand-all"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setAllSections(false)}
            className="text-[11px] text-[#6B6B7A] hover:text-[#1E1E24] px-2 py-1 rounded"
            data-testid="runsheet-collapse-all"
          >
            Collapse all
          </button>
          <button
            type="button"
            onClick={fetchToday}
            className="inline-flex items-center gap-1 text-xs text-[#6B6B7A] hover:text-[#1E1E24] rounded-full px-3 py-1.5 border border-[#E8E5DF] hover:border-[#FF3B6B]/40"
            data-testid="runsheet-refresh"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF3B6B]" />
        </div>
      ) : (
        <>
          {/* ============ BOOKINGS ============ */}
          <SectionHeader
            icon={CalendarIcon} title="Bookings" count={parties.length} accent="#FF3B6B"
            expanded={sections.bookings} onToggle={() => toggleSection('bookings')}
            testId="runsheet-section-bookings"
          />
          {sections.bookings && (parties.length === 0 ? (
            <p className="text-xs text-[#6B6B7A] italic py-2">No parties today. Enjoy the quiet moment.</p>
          ) : (
            <ol className="space-y-2" data-testid="runsheet-list">
              {parties.map((p, i) => {
                const color = PARTY_COLORS[i % PARTY_COLORS.length];
                return (
                  <motion.li
                    key={p.booking_id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => setDetailsBookingId(p.booking_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setDetailsBookingId(p.booking_id)}
                    className="grid grid-cols-[80px_1fr_auto] items-center gap-4 p-3 rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] hover:bg-white hover:shadow-md cursor-pointer transition-all"
                    style={{ borderLeft: `4px solid ${color}` }}
                    data-testid={`runsheet-row-${p.booking_id}`}
                  >
                    <div className="text-center">
                      <div className="inline-flex items-center gap-1 text-[#1E1E24] font-semibold text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                        <Clock className="w-3.5 h-3.5" style={{ color }} />
                        {p.time_slot}
                      </div>
                      <p className="text-[10px] mt-0.5">
                        {p.arrived ? (
                          <span className="inline-flex items-center gap-0.5 text-[#06D6A0] font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Arrived
                          </span>
                        ) : <span className="text-[#6B6B7A]">Scheduled</span>}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1E1E24] truncate" data-testid={`runsheet-child-${p.booking_id}`}>
                          {p.child_name} ({p.child_age})
                        </span>
                        <span className="text-xs text-[#6B6B7A]">· {p.customer_name}</span>
                        {p.has_allergies && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ backgroundColor: '#FFE4E1', color: '#D1344D' }}
                            title={`Allergies: ${p.allergies.join(', ')}`}
                            data-testid={`runsheet-allergy-${p.booking_id}`}
                          >
                            <AlertTriangle className="w-3 h-3" /> Allergies
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-[#6B6B7A] flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" /> {p.guest_count} guests
                        </span>
                        <span className="truncate">{p.package_name}</span>
                        <span className="inline-flex items-center gap-1 truncate">
                          <UserCheck className="w-3 h-3" />
                          {p.booked_by_employee_name || <span className="italic text-[#9a9aa6]">Unassigned</span>}
                        </span>
                      </div>
                      {p.arrived && p.arrived_at && (
                        <div className="mt-1.5" data-testid={`runsheet-timer-wrap-${p.booking_id}`}>
                          <PartyTimer arrivedAt={p.arrived_at} testId={`runsheet-timer-${p.booking_id}`} />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button" onClick={(e) => printChecklist(p, e)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B6B7A] hover:bg-[#FFF0F3] hover:text-[#FF3B6B]"
                        title="Print day-of checklist"
                        data-testid={`runsheet-print-${p.booking_id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        type="button" onClick={(e) => messageHost(p, e)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${p.booked_by_employee_id ? 'text-[#6366F1] hover:bg-[#F0F0FF]' : 'text-[#C8C8D0] cursor-not-allowed'}`}
                        title={p.booked_by_employee_id ? `Message ${p.booked_by_employee_name}` : 'No host assigned'}
                        data-testid={`runsheet-message-${p.booking_id}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => toggleArrived(p.booking_id, e)}
                        disabled={busyId === p.booking_id}
                        className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${p.arrived ? 'bg-[#06D6A0] text-white hover:bg-[#05b989]' : 'bg-white border border-[#E8E5DF] text-[#1E1E24] hover:border-[#06D6A0] hover:text-[#06D6A0]'} disabled:opacity-50`}
                        data-testid={`runsheet-arrived-${p.booking_id}`}
                      >
                        {p.arrived ? 'Arrived' : 'Mark arrived'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => toggleComplete(p.booking_id, !!p.party_completed, e)}
                        disabled={busyId === p.booking_id}
                        className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${p.party_completed ? 'bg-[#9333EA] text-white hover:bg-[#7d2bce]' : 'bg-white border border-[#E8E5DF] text-[#1E1E24] hover:border-[#9333EA] hover:text-[#9333EA]'} disabled:opacity-50`}
                        data-testid={`runsheet-complete-${p.booking_id}`}
                        title={p.party_completed ? 'Click to un-mark complete' : 'Mark party complete (queues inventory deductions)'}
                      >
                        {p.party_completed ? 'Complete ✓' : 'Mark complete'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => markNoShow(p, e)}
                        disabled={busyId === p.booking_id}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B6B7A] hover:bg-[#FFF4D6] hover:text-[#B8860B] disabled:opacity-40"
                        title="Mark as No Show"
                        data-testid={`runsheet-noshow-${p.booking_id}`}
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => openDelete(p, e)}
                        disabled={busyId === p.booking_id}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B6B7A] hover:bg-[#FFE4E1] hover:text-[#D1344D] disabled:opacity-40"
                        title="Delete booking"
                        data-testid={`runsheet-delete-${p.booking_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.li>
                );
              })}
            </ol>
          ))}

          {/* ============ EMPLOYEE SHIFTS ============ */}
          <SectionHeader
            icon={Briefcase} title="Employee Shifts" count={shifts.length} accent="#6366F1"
            expanded={sections.shifts} onToggle={() => toggleSection('shifts')}
            testId="runsheet-section-shifts"
          />
          {sections.shifts && (shifts.length === 0 ? (
            <p className="text-xs text-[#6B6B7A] italic py-2">No shifts scheduled for today.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="runsheet-shifts">
              {shifts.map((s) => {
                const live = !!s.on_the_clock;
                return (
                <li
                  key={s.shift_id}
                  onClick={() => setShiftDetail(s)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setShiftDetail(s)}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${live ? 'bg-[#EDFFF8] border-[#06D6A0]/40 shadow-[0_0_0_3px_rgba(6,214,160,0.12)]' : 'bg-[#F0F0FF] hover:bg-[#E2E2FF] border-[#6366F1]/15'}`}
                  data-testid={`runsheet-shift-${s.shift_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center bg-white font-bold text-xs relative ${live ? 'text-[#06D6A0] ring-2 ring-[#06D6A0]' : 'text-[#6366F1]'}`}
                      aria-hidden
                    >
                      {(s.employee_name || '—').slice(0, 1).toUpperCase()}
                      {live && (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#06D6A0] ring-2 ring-white animate-pulse"
                          aria-hidden
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1E1E24] truncate flex items-center gap-1.5">
                        {s.employee_name}
                        {live && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#06D6A0]/15 text-[#05a07a]"
                            data-testid={`runsheet-shift-live-${s.shift_id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#06D6A0] animate-pulse" aria-hidden />
                            {s.on_break ? 'on break' : 'on the clock'}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#6B6B7A] capitalize">
                        {s.start_time} – {s.end_time} · {(s.role || '—').replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#9a9aa6] flex-shrink-0" />
                </li>
                );
              })}
            </ul>
          ))}

          {/* ============ SPECIAL TASKS ============ */}
          <SectionHeader
            icon={Cake} title="Special Tasks" count={special_tasks.length} accent="#FFD166"
            expanded={sections.tasks} onToggle={() => toggleSection('tasks')}
            testId="runsheet-section-tasks"
          />
          {sections.tasks && (special_tasks.length === 0 ? (
            <p className="text-xs text-[#6B6B7A] italic py-2">No cake pickups, prints or custom tasks pending today.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="runsheet-tasks">
              {special_tasks.map((t, i) => {
                const key = `${t.booking_id}:${t.type}:${i}`;
                const done = !!doneTasks[key];
                const Icon = t.type === 'custom_cake' ? Cake : Shirt;
                const accent = t.type === 'custom_cake' ? '#FFD166' : '#FF7A2E';
                return (
                  <li
                    key={key}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-all ${done ? 'bg-[#EDFFF8] border-[#06D6A0]/30' : 'bg-[#FFF8E7] border-[#FFD166]/30 hover:bg-[#FFF4D6]'}`}
                    onClick={() => setDoneTasks(prev => ({ ...prev, [key]: !prev[key] }))}
                    data-testid={`runsheet-task-${i}`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setDoneTasks(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {done ? (
                        <CheckCircle2 className="w-5 h-5 text-[#06D6A0] flex-shrink-0" />
                      ) : (
                        <Icon className="w-5 h-5 flex-shrink-0" style={{ color: accent }} />
                      )}
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${done ? 'line-through text-[#6B6B7A]' : 'text-[#1E1E24]'}`}>{t.label}</p>
                        <p className="text-[10px] text-[#6B6B7A]">
                          For {t.child_name} · {t.time_slot}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDetailsBookingId(t.booking_id); }}
                      className="text-[11px] font-semibold text-[#6366F1] hover:underline flex-shrink-0"
                      data-testid={`runsheet-task-view-${i}`}
                    >
                      View booking →
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}

          {parties.length > 0 && (
            <div className="mt-4 text-right">
              <button
                type="button"
                onClick={() => navigate(`/admin?view=byDate&date_from=${new Date().toISOString().slice(0, 10)}`)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#6B6B7A] hover:text-[#1E1E24]"
                data-testid="runsheet-view-all"
              >
                See full schedule <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Details dialogs */}
      <BookingDetailsDialog
        bookingId={detailsBookingId}
        open={!!detailsBookingId}
        onOpenChange={(v) => !v && setDetailsBookingId(null)}
      />
      <ShiftDetailsDialog
        shift={shiftDetail}
        open={!!shiftDetail}
        onOpenChange={(v) => !v && setShiftDetail(null)}
      />
      <DeleteBookingDialog
        booking={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onDeleted={onBookingDeleted}
      />
      <span className="hidden"><X /></span>
    </div>
  );
};

export default TodayRunSheet;
